import i18next from "i18next";

type Resources = Record<string, { common: Record<string, string> }>;
interface ILanguages {
  languages: string[];
  default: string;
}

export const DEFAULT_LANGUAGE = "en";
const STORAGE_LANGUAGE = "appLanguage";
let settings: ILanguages | undefined;
const FALLBACK_SETTINGS: ILanguages = {
  languages: [DEFAULT_LANGUAGE],
  default: DEFAULT_LANGUAGE,
};
const LANGUAGE_ALIASES: Record<string, string> = {
  telefonica_es: "es",
};

const modules = import.meta.glob<{ default: Record<string, string> }>(
  "../i18n/*/common.json",
  { eager: true },
);

export const bundledResources: Resources = Object.fromEntries(
  Object.entries(modules).map(([path, mod]) => [
    path.split("/")[2],
    { common: mod.default },
  ]),
);

const parseLanguageSettings = (): ILanguages => {
  try {
    const parsed = JSON.parse(import.meta.env.VITE_LANGUAGE_SETTINGS);
    const languages = (parsed.languages as string[]).filter(
      (lng) => lng in bundledResources,
    );
    if (!languages.length) return FALLBACK_SETTINGS;
    const preferred = [parsed.default, DEFAULT_LANGUAGE];
    return {
      languages,
      default: preferred.find((lng) => languages.includes(lng)) ?? languages[0],
    };
  } catch {
    return FALLBACK_SETTINGS;
  }
};

export const getLanguages = (): ILanguages => {
  if (!settings) settings = parseLanguageSettings();
  return settings;
};

const toBaseCode = (language: string) =>
  language.toLowerCase().split(/[-_]/)[0];

const matchSupported = (language: string): string | undefined => {
  const { languages } = getLanguages();
  const aliased = LANGUAGE_ALIASES[language] ?? language;
  if (languages.includes(aliased)) return aliased;
  const base = toBaseCode(aliased);
  return languages.find((lng) => toBaseCode(lng) === base);
};

export const resolveLanguage = (language?: string | null): string =>
  (language ? matchSupported(language) : undefined) ?? getLanguages().default;

// scan navigator.languages
export const detectBrowserLanguage = (): string | undefined => {
  const nav = globalThis.navigator;
  const preferred = nav?.languages?.length ? nav.languages : [nav?.language];
  for (const candidate of preferred) {
    const match = candidate ? matchSupported(candidate) : undefined;
    if (match) return match;
  }
  return undefined;
};

export const getStoredLanguage = (): string | undefined => {
  try {
    const stored = localStorage.getItem(STORAGE_LANGUAGE);
    return stored ? matchSupported(stored) : undefined;
  } catch {
    return undefined;
  }
};

export const setStoredLanguage = (language: string) => {
  try {
    localStorage.setItem(STORAGE_LANGUAGE, language);
  } catch {
    return undefined;
  }
};

// get default language from the local setting, then browser or device language
export const getAppLanguage = (saved?: string | null): string =>
  resolveLanguage(saved || getStoredLanguage() || detectBrowserLanguage());

export const withLanguage = (
  url: string,
  language: string = i18next.language || DEFAULT_LANGUAGE,
) => `${url}${url.includes("?") ? "&" : "?"}lang=${language}`;
